import React, { useEffect, useRef, useState } from 'react';
import './AirplaneAnimation.css';

const AirplaneAnimation = () => {
  const [hasImageError, setHasImageError] = useState(false);
  const airplaneRef = useRef(null);
  const frameRef = useRef(null);
  const lastStateRef = useRef({
    time: 0,
    scrollY: 0,
    scrollVelocity: 0,
    filteredVelocity: 0,
    flameLength: 16,
    flameOpacity: 0,
    planeY: 0,
  });

  useEffect(() => {
    const lastState = lastStateRef.current;
    lastState.time = performance.now();
    lastState.scrollY = window.scrollY || 0;
    lastState.scrollVelocity = 0;
    lastState.filteredVelocity = 0;
    lastState.flameLength = 16;
    lastState.flameOpacity = 0;
    lastState.planeY = 0;

    const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

    const tick = (now) => {
      const currentScrollY = window.scrollY || 0;
      const dt = (now - lastState.time) / 1000;

      if (dt > 0) {
        const scrollVelocity = (currentScrollY - lastState.scrollY) / dt;
        // 低通滤波，减小抖动，保持丝滑（时间常数）
        const tau = 0.12;
        const alpha = 1 - Math.exp(-dt / tau);
        const filteredVelocity =
          lastState.filteredVelocity +
          (scrollVelocity - lastState.filteredVelocity) * alpha;

        // 飞机速度与用户滑动速度大小相等、方向相反
        const planeVelocity = -filteredVelocity * 1.2;

        lastState.planeY += planeVelocity * dt;
        if (currentScrollY <= 0) {
          lastState.planeY = 0;
        }
        lastState.scrollVelocity = scrollVelocity;
        lastState.filteredVelocity = filteredVelocity;
        lastState.scrollY = currentScrollY;
        lastState.time = now;

        if (airplaneRef.current) {
          const speed = Math.abs(filteredVelocity);
          const minFlame = 12;
          const maxFlame = 90;
          const targetFlame = clamp(minFlame + speed * 0.08, minFlame, maxFlame);
          const targetOpacity = speed > 6 ? 1 : 0;
          const flameSmoothing = 0.18;
          lastState.flameLength =
            lastState.flameLength +
            (targetFlame - lastState.flameLength) * flameSmoothing;
          lastState.flameOpacity =
            lastState.flameOpacity +
            (targetOpacity - lastState.flameOpacity) * 0.25;
          airplaneRef.current.style.setProperty('--plane-y', `${lastState.planeY.toFixed(2)}px`);
          airplaneRef.current.style.setProperty('--flame-length', `${lastState.flameLength.toFixed(2)}px`);
          airplaneRef.current.style.setProperty('--flame-opacity', `${lastState.flameOpacity.toFixed(2)}`);
        }
      } else {
        lastState.time = now;
      }

      frameRef.current = requestAnimationFrame(tick);
    };

    frameRef.current = requestAnimationFrame(tick);

    return () => {
      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current);
      }
    };
  }, []);

  return (
    <div className="airplane-animation">
      {/* 飞机图案 */}
      <div className="airplane" ref={airplaneRef}>
        <div className="airplane-flame" aria-hidden="true"></div>
        {hasImageError ? (
          <svg
            width="120"
            height="60"
            viewBox="0 0 120 60"
            role="img"
            aria-label="飞机图案"
          >
            <title>飞机图案</title>
            <ellipse cx="60" cy="30" rx="50" ry="8" fill="#666" />
            <ellipse cx="110" cy="30" rx="8" ry="6" fill="#666" />
            <polygon points="40,15 60,30 40,45" fill="#666" />
            <polygon points="80,15 60,30 80,45" fill="#666" />
            <polygon points="20,10 30,30 20,50" fill="#666" />
            <polygon points="90,10 100,30 90,50" fill="#666" />
            <circle cx="70" cy="30" r="2" fill="#999" />
            <circle cx="80" cy="30" r="2" fill="#999" />
            <circle cx="90" cy="30" r="2" fill="#999" />
          </svg>
        ) : (
          <img
            src="/images/airplane.png"
            alt="飞机图案"
            width="120"
            height="60"
            loading="lazy"
            decoding="async"
            onError={() => setHasImageError(true)}
          />
        )}
      </div>
    </div>
  );
};

export default AirplaneAnimation;
